import Phaser from "phaser";
import {
  DIRECTIONS,
  MAP_LAYERS,
  PILL_CONFIG,
  POWER_PILL_POSITIONS,
  RENDER_DEPTHS,
} from "../../constants/game";
import { NumberPill, Point, QuestionData } from "../../types/game";

/**
 * PillManager
 * Handles all number pill placement, validation, and management
 */
export class PillManager {
  private scene: Phaser.Scene;
  private map: Phaser.Tilemaps.Tilemap;
  private blockSize: number;
  private dots: Phaser.Physics.Arcade.Group;
  private pills: Phaser.Physics.Arcade.Group;

  // Pre-computed valid pill locations (computed once during initialization)
  private validTwoDigitLocations: Point[] = [];
  private validThreeDigitLocations: Point[] = [];

  // Current correct answer for validation
  private correctAnswer?: number;

  // Callback to request question data from React
  private requestQuestion?: () => QuestionData | null;

  // Callback to get Pacman's current position
  private getPacmanPosition?: () => Point | null;

  // Callback to get Pacman's current direction
  private getPacmanDirection?: () => string | null;

  // Flag to track if pills have been placed for the current question
  // Prevents duplicate pill placement when game state changes to PLAYING
  private pillsPlaced: boolean = false;

  constructor(
    scene: Phaser.Scene,
    map: Phaser.Tilemaps.Tilemap,
    blockSize: number,
    dots: Phaser.Physics.Arcade.Group,
    pills: Phaser.Physics.Arcade.Group
  ) {
    this.scene = scene;
    this.map = map;
    this.blockSize = blockSize;
    this.dots = dots;
    this.pills = pills;
  }

  /**
   * Set the callback function to request question data from React
   * @param callback - Function that returns question data or null
   */
  setRequestQuestionCallback(callback: () => QuestionData | null): void {
    this.requestQuestion = callback;
  }

  /**
   * Set the callback function to get Pacman's current position
   * @param callback - Function that returns Pacman's position or null
   */
  setPacmanPositionCallback(callback: () => Point | null): void {
    this.getPacmanPosition = callback;
  }

  /**
   * Set the callback function to get Pacman's current direction
   * @param callback - Function that returns Pacman's direction or null
   */
  setPacmanDirectionCallback(callback: () => string | null): void {
    this.getPacmanDirection = callback;
  }

  /**
   * Clear the pills placed flag
   * Call this when a pill is eaten or when starting a new game
   */
  clearPillsPlacedFlag(): void {
    this.pillsPlaced = false;
  }

  /**
   * Mark pills as placed
   * Call this when pills are placed externally (e.g., by React)
   */
  markPillsAsPlaced(): void {
    this.pillsPlaced = true;
  }

  /**
   * Automatically place pills when game enters PLAYING state
   * Requests question data from React and places pills if available
   * Will retry if question data is not available (e.g., game config not loaded yet)
   * @returns Promise<boolean> - Success status
   */
  async placePillsIfNeeded(): Promise<boolean> {
    console.log("placePillsIfNeeded called, pillsPlaced:", this.pillsPlaced);

    // Prevent duplicate pill placement
    if (this.pillsPlaced) {
      console.log("Pills already placed for current question, skipping");
      return false;
    }

    // Check if callback is available
    if (!this.requestQuestion) {
      console.warn("Question request callback not available");
      return false;
    }

    console.log("Requesting question data from React...");
    // Request question data from React
    const questionData = this.requestQuestion();
    console.log("Question data received:", questionData);

    if (!questionData) {
      console.warn(
        "No question data available (game config may not be loaded yet)"
      );
      // Don't mark as placed, so it can retry when game config becomes available
      return false;
    }

    console.log("Calling placeNumberPills with:", questionData);
    // Place pills with the question data
    const success = await this.placeNumberPills(
      questionData.answers,
      questionData.correctAnswer
    );

    if (success) {
      this.pillsPlaced = true;
      console.log("Pills placed automatically by PillManager");
    } else {
      console.error("Failed to place pills automatically");
    }

    return success;
  }

  /**
   * Compute all valid pill locations during initialization
   * Called once when the game is created
   */
  computeValidPillLocations(): void {
    this.validTwoDigitLocations = this.getAllValidDotLocations();
    this.validThreeDigitLocations = this.findHorizontalLines(
      PILL_CONFIG.MIN_HORIZONTAL_LENGTH
    );

    // Filter out locations that overlap with power pills
    this.validTwoDigitLocations = this.filterPowerPillOverlaps(
      this.validTwoDigitLocations
    );
    this.validThreeDigitLocations = this.filterPowerPillOverlaps(
      this.validThreeDigitLocations
    );
  }

  /**
   * Filter out locations that are too close to power pills
   * @param locations - Array of potential pill locations
   * @returns Filtered array excluding power pill overlaps
   */
  private filterPowerPillOverlaps(locations: Point[]): Point[] {
    const minDistance = this.blockSize * 3; // Keep 3 block distance from power pills

    return locations.filter((location) => {
      // Check if this location is too close to any power pill
      for (const powerPill of POWER_PILL_POSITIONS) {
        const distance = Math.hypot(
          location.x - powerPill.x,
          location.y - powerPill.y
        );
        if (distance < minDistance) {
          return false; // Too close to a power pill, exclude this location
        }
      }
      return true; // Safe distance from all power pills
    });
  }

  /**
   * Get all valid dot locations for 2-digit pills
   * Returns an array of pixel coordinates where dots can be placed
   * @returns Array of points representing valid dot locations
   */
  private getAllValidDotLocations(): Point[] {
    const validLocations: Point[] = [];
    const layer = this.map.getLayer(MAP_LAYERS.TILE_LAYER_1)?.tilemapLayer;
    if (!layer) return validLocations;

    layer.forEachTile((tile) => {
      // Skip restricted areas (same logic as populateBoardAndTrackEmptyTiles)
      if (
        tile.y < 1 ||
        (tile.y > 10 && tile.y < 20 && tile.x > 6 && tile.x < 21) ||
        (tile.y === 14 && tile.x !== 6 && tile.x !== 21)
      )
        return;

      const rightTile = this.map.getTileAt(
        tile.x + 1,
        tile.y,
        true,
        "Tile Layer 1"
      );
      const bottomTile = this.map.getTileAt(
        tile.x,
        tile.y + 1,
        true,
        "Tile Layer 1"
      );
      const rightBottomTile = this.map.getTileAt(
        tile.x + 1,
        tile.y + 1,
        true,
        "Tile Layer 1"
      );

      // Check if 2x2 area is empty (same logic as dot placement)
      if (
        tile.index === -1 &&
        rightTile &&
        rightTile.index === -1 &&
        bottomTile &&
        bottomTile.index === -1 &&
        rightBottomTile &&
        rightBottomTile.index === -1
      ) {
        const x = tile.x * this.blockSize + this.blockSize;
        const y = tile.y * this.blockSize + this.blockSize;
        validLocations.push({ x, y });
      }
    });

    return validLocations;
  }

  /**
   * Find horizontal lines with at least minLength consecutive empty tiles
   * Used for placing 3-digit pills
   * @param minLength - Minimum number of consecutive tiles required
   * @returns Array of points representing center positions for horizontal lines
   */
  private findHorizontalLines(
    minLength: number = PILL_CONFIG.MIN_HORIZONTAL_LENGTH
  ): Point[] {
    const validLines: Point[] = [];
    const layer = this.map.getLayer(MAP_LAYERS.TILE_LAYER_1)?.tilemapLayer;
    if (!layer) return validLines;

    // Group tiles by row (y coordinate)
    const rows: Map<number, Phaser.Tilemaps.Tile[]> = new Map();

    layer.forEachTile((tile) => {
      // Skip restricted areas
      if (
        tile.y < 1 ||
        (tile.y > 10 && tile.y < 20 && tile.x > 6 && tile.x < 21) ||
        (tile.y === 14 && tile.x !== 6 && tile.x !== 21)
      )
        return;

      if (!rows.has(tile.y)) {
        rows.set(tile.y, []);
      }
      rows.get(tile.y)?.push(tile);
    });

    // Find horizontal lines on each row - generate multiple valid locations from each sequence
    rows.forEach((tiles, y) => {
      if (tiles.length === 0) return;

      // Find all consecutive horizontal sequences of empty tiles
      let currentSequenceStart = -1;
      let currentSequenceLength = 0;

      for (let x = 0; x < this.map.width; x++) {
        const tile = this.map.getTileAt(x, y, true, "Tile Layer 1");

        // Check if tile is empty AND not in restricted areas
        const isInRestrictedArea =
          y < 1 ||
          (y > 10 && y < 20 && x > 6 && x < 21) ||
          (y === 14 && x !== 6 && x !== 21);

        // Also check if this is a valid dot location (2x2 empty area)
        // This ensures pills are placed in safe zones, not on boundaries
        let isValidDotArea = false;
        if (tile && tile.index === -1 && !isInRestrictedArea) {
          const rightTile = this.map.getTileAt(x + 1, y, true, "Tile Layer 1");
          const bottomTile = this.map.getTileAt(x, y + 1, true, "Tile Layer 1");
          const rightBottomTile = this.map.getTileAt(
            x + 1,
            y + 1,
            true,
            "Tile Layer 1"
          );

          isValidDotArea = !!(
            rightTile &&
            rightTile.index === -1 &&
            bottomTile &&
            bottomTile.index === -1 &&
            rightBottomTile &&
            rightBottomTile.index === -1
          );
        }

        if (isValidDotArea) {
          // Valid dot area - continue or start a sequence
          if (currentSequenceStart === -1) {
            currentSequenceStart = x;
            currentSequenceLength = 1;
          } else {
            // Check if this tile is consecutive to the previous one
            if (x === currentSequenceStart + currentSequenceLength) {
              currentSequenceLength++;
            } else {
              // Gap in sequence, process previous sequence and start new one
              if (currentSequenceLength >= minLength) {
                // Generate multiple pill locations from this sequence
                const maxStartOffset = currentSequenceLength - minLength;
                for (let offset = 0; offset <= maxStartOffset; offset++) {
                  const pillCenterTileX =
                    currentSequenceStart +
                    offset +
                    Math.floor((minLength - 1) / 2);
                  const pillX =
                    pillCenterTileX * this.blockSize + this.blockSize;
                  const pillY = y * this.blockSize + this.blockSize;
                  validLines.push({ x: pillX, y: pillY });
                }
              }
              // Start new sequence
              currentSequenceStart = x;
              currentSequenceLength = 1;
            }
          }
        } else {
          // Not a valid dot area, process any existing sequence
          if (currentSequenceLength >= minLength) {
            const maxStartOffset = currentSequenceLength - minLength;
            for (let offset = 0; offset <= maxStartOffset; offset++) {
              const pillCenterTileX =
                currentSequenceStart + offset + Math.floor((minLength - 1) / 2);
              const pillX = pillCenterTileX * this.blockSize + this.blockSize;
              const pillY = y * this.blockSize + this.blockSize;
              validLines.push({ x: pillX, y: pillY });
            }
          }
          // Reset sequence tracking
          currentSequenceStart = -1;
          currentSequenceLength = 0;
        }
      }

      // Process final sequence if row ended with valid dot areas
      if (currentSequenceLength >= minLength) {
        const maxStartOffset = currentSequenceLength - minLength;
        for (let offset = 0; offset <= maxStartOffset; offset++) {
          const pillCenterTileX =
            currentSequenceStart + offset + Math.floor((minLength - 1) / 2);
          const pillX = pillCenterTileX * this.blockSize + this.blockSize;
          const pillY = y * this.blockSize + this.blockSize;
          validLines.push({ x: pillX, y: pillY });
        }
      }
    });

    return validLines;
  }

  /**
   * Remove a dot at the specified location
   * @param x - X coordinate in pixels
   * @param y - Y coordinate in pixels
   */
  removeDotAtLocation(x: number, y: number): void {
    this.dots.children.entries.forEach((dot) => {
      const dotSprite = dot as Phaser.GameObjects.GameObject & {
        x: number;
        y: number;
        disableBody: (
          removeFromWorld: boolean,
          disableGameObject: boolean
        ) => void;
      };
      if (
        Math.abs(dotSprite.x - x) < this.blockSize &&
        Math.abs(dotSprite.y - y) < this.blockSize
      ) {
        dotSprite.disableBody(true, true);
      }
    });
  }

  /**
   * Create a pill sprite with border and number text
   * @param x - X coordinate for pill center
   * @param y - Y coordinate for pill center
   * @param number - The number to display on the pill
   * @param digitCount - Number of digits (2 or 3)
   * @returns Created pill container with physics body
   */
  createPillSprite(
    x: number,
    y: number,
    number: number,
    digitCount: number
  ): NumberPill {
    const width =
      digitCount === 2
        ? PILL_CONFIG.PILL_WIDTH_2DIGIT
        : PILL_CONFIG.PILL_WIDTH_3DIGIT;
    const height = PILL_CONFIG.PILL_HEIGHT;

    // Create container for the pill
    const pill = this.scene.add.container(x, y) as NumberPill;

    // Create pill-shaped border (rounded rectangle)
    const graphics = this.scene.add.graphics();
    // Clear any previous drawing
    graphics.clear();
    // Fill white background
    graphics.fillStyle(0xd7edfb, 1);
    graphics.fillRoundedRect(
      -width / 2,
      -height / 2,
      width,
      height,
      height / 2
    );
    // Yellow border
    graphics.lineStyle(2, 0x47b8ae, 1);
    graphics.strokeRoundedRect(
      -width / 2,
      -height / 2,
      width,
      height,
      height / 2
    );

    // Make graphics visible
    graphics.setVisible(true);
    graphics.setActive(true);

    // Create bold number text
    const textStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontSize: "60px",
      fontFamily: "Nunito Sans",
      color: "#245C57",
      fontStyle: "bold",
    };
    const numberText = this.scene.add.text(0, 0, number.toString(), textStyle);
    numberText.setOrigin(0.5, 0.5);
    numberText.setVisible(true);

    // Add graphics and text to container
    pill.add([graphics, numberText]);
    pill.setDepth(RENDER_DEPTHS.PILLS);
    pill.setVisible(true);

    // Store number and digit count in pill object
    pill.number = number;
    pill.digitCount = digitCount;

    return pill;
  }

  /**
   * Place number pills on the game board
   * Called from App.tsx with 2 answer numbers
   * @param answers - Array of 2 answer numbers (1 correct + 1 wrong)
   * @param correctAnswer - The correct answer number
   * @returns Promise<boolean> - Success status
   */
  async placeNumberPills(
    answers: number[],
    correctAnswer: number
  ): Promise<boolean> {
    try {
      if (!answers || answers.length !== 2) {
        return false;
      }

      // Validate correctAnswer before proceeding
      if (
        correctAnswer === undefined ||
        correctAnswer === null ||
        isNaN(correctAnswer)
      ) {
        console.error(
          "Invalid correctAnswer provided to placeNumberPills:",
          correctAnswer
        );
        return false;
      }

      // Clear existing pills - manually remove all to avoid errors
      if (this.pills && this.pills.children && this.pills.children.entries) {
        const pillsToRemove: Phaser.GameObjects.GameObject[] = [];
        // Collect ALL pills, not just active ones
        this.pills.children.entries.forEach((pill) => {
          if (pill) {
            pillsToRemove.push(pill);
          }
        });
        // Properly remove each pill from physics group and destroy it
        pillsToRemove.forEach((pill) => {
          const pillWithBody = pill as Phaser.GameObjects.GameObject & {
            body?: Phaser.Physics.Arcade.Body;
            destroy: () => void;
          };
          if (pillWithBody.body) {
            this.scene.physics.world.disableBody(pillWithBody.body);
          }
          if (this.pills.contains(pill)) {
            this.pills.remove(pill, true, true);
          }
          pillWithBody.destroy();
        });
      }

      // Store correct answer for validation
      this.correctAnswer = correctAnswer;

      // Determine digit count for each answer
      const digitCounts = answers.map((answer) =>
        answer.toString().length === 3 ? 3 : 2
      );

      // Separate answers by digit count
      const twoDigitAnswers: { answer: number; index: number }[] = [];
      const threeDigitAnswers: { answer: number; index: number }[] = [];

      answers.forEach((answer, index) => {
        if (digitCounts[index] === 2) {
          twoDigitAnswers.push({ answer, index });
        } else {
          threeDigitAnswers.push({ answer, index });
        }
      });

      // Use pre-computed valid locations - create copies to avoid modifying originals
      const availableTwoDigitLocations = [...this.validTwoDigitLocations];
      const availableThreeDigitLocations = [...this.validThreeDigitLocations];

      // Track placed pill positions to prevent overlaps
      const placedPillPositions: Point[] = [];

      // Get Pacman's current position and direction
      const pacmanPosition = this.getPacmanPosition?.() || null;
      const pacmanDirection = this.getPacmanDirection?.() || null;

      // Calculate minimum distance between pills (larger pill width + padding)
      // Use 3-digit pill width (144px) + 1 block padding (48px) = 192px minimum
      const minPillDistance =
        Math.max(PILL_CONFIG.PILL_WIDTH_2DIGIT, PILL_CONFIG.PILL_WIDTH_3DIGIT) +
        this.blockSize;

      // Minimum distance from Pacman (4 blocks = 192px to ensure safe spacing)
      const minPacmanDistance = this.blockSize * 4;

      // Helper function to check if a location is in Pacman's current direction
      const isInPacmanDirection = (location: Point): boolean => {
        if (!pacmanPosition || !pacmanDirection) return false;

        switch (pacmanDirection) {
          case DIRECTIONS.RIGHT:
            return location.x > pacmanPosition.x;
          case DIRECTIONS.LEFT:
            return location.x < pacmanPosition.x;
          case DIRECTIONS.UP:
            return location.y < pacmanPosition.y;
          case DIRECTIONS.DOWN:
            return location.y > pacmanPosition.y;
          default:
            return false;
        }
      };

      // Helper function to filter locations that are too close to placed pills or Pacman
      // Also tries to avoid locations in Pacman's direction (preference, not requirement)
      const filterLocationsByDistance = (
        locations: Point[],
        placedPositions: Point[]
      ): Point[] => {
        // First filter by distance constraints (required)
        const distanceFiltered = locations.filter((location) => {
          // Check distance from already-placed pills
          const tooCloseToPill = placedPositions.some((placed) => {
            const distance = Math.hypot(
              location.x - placed.x,
              location.y - placed.y
            );
            return distance < minPillDistance;
          });

          // Check distance from Pacman
          const tooCloseToPacman =
            pacmanPosition &&
            Math.hypot(
              location.x - pacmanPosition.x,
              location.y - pacmanPosition.y
            ) < minPacmanDistance;

          // Keep location only if it's far enough from both pills and Pacman
          return !tooCloseToPill && !tooCloseToPacman;
        });

        // If we have locations not in Pacman's direction, prefer those
        const nonDirectionLocations = distanceFiltered.filter(
          (location) => !isInPacmanDirection(location)
        );

        // If possible, avoid placing in Pacman's direction
        // But if no other locations are available, allow direction locations
        return nonDirectionLocations.length > 0
          ? nonDirectionLocations
          : distanceFiltered;
      };

      // Place 2-digit pills
      let twoDigitPlaced = 0;
      for (const { answer } of twoDigitAnswers) {
        // Filter out locations too close to already-placed pills
        const validLocations = filterLocationsByDistance(
          availableTwoDigitLocations,
          placedPillPositions
        );

        if (validLocations.length > 0) {
          // Pick random index from valid locations
          const randomIndex = Math.floor(Math.random() * validLocations.length);
          const location = validLocations[randomIndex];

          // Remove from available locations
          const indexInAvailable = availableTwoDigitLocations.findIndex(
            (loc) => loc.x === location.x && loc.y === location.y
          );
          if (indexInAvailable !== -1) {
            availableTwoDigitLocations.splice(indexInAvailable, 1);
          }

          const pill = this.createPillSprite(location.x, location.y, answer, 2);
          // Enable physics for collision detection
          this.scene.physics.world.enable(pill);
          const body = pill.body as Phaser.Physics.Arcade.Body;
          const width = PILL_CONFIG.PILL_WIDTH_2DIGIT;
          body.setSize(width, PILL_CONFIG.PILL_HEIGHT);
          body.setOffset(-width / 2, -PILL_CONFIG.PILL_HEIGHT / 2);
          this.pills.add(pill);
          this.removeDotAtLocation(location.x, location.y);

          // Track this pill's position
          placedPillPositions.push({ x: location.x, y: location.y });
          twoDigitPlaced++;
        }
      }

      // Place 3-digit pills
      let threeDigitPlaced = 0;
      for (const { answer } of threeDigitAnswers) {
        // Filter out locations too close to already-placed pills
        const validLocations = filterLocationsByDistance(
          availableThreeDigitLocations,
          placedPillPositions
        );

        if (validLocations.length > 0) {
          // Pick random index from valid locations
          const randomIndex = Math.floor(Math.random() * validLocations.length);
          const location = validLocations[randomIndex];

          // Remove from available locations
          const indexInAvailable = availableThreeDigitLocations.findIndex(
            (loc) => loc.x === location.x && loc.y === location.y
          );
          if (indexInAvailable !== -1) {
            availableThreeDigitLocations.splice(indexInAvailable, 1);
          }

          const pill = this.createPillSprite(location.x, location.y, answer, 3);
          // Enable physics for collision detection
          this.scene.physics.world.enable(pill);
          const body = pill.body as Phaser.Physics.Arcade.Body;
          const width = PILL_CONFIG.PILL_WIDTH_3DIGIT;
          body.setSize(width, PILL_CONFIG.PILL_HEIGHT);
          body.setOffset(-width / 2, -PILL_CONFIG.PILL_HEIGHT / 2);
          this.pills.add(pill);
          this.removeDotAtLocation(location.x, location.y);

          // Track this pill's position
          placedPillPositions.push({ x: location.x, y: location.y });
          threeDigitPlaced++;
        }
      }

      if (twoDigitPlaced + threeDigitPlaced < answers.length) {
        return false;
      }

      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get the correct answer for validation
   */
  getCorrectAnswer(): number | undefined {
    return this.correctAnswer;
  }

  /**
   * Get current pill locations
   * Used by BoardManager to avoid placing dots on pills
   */
  getCurrentPillLocations(): Point[] {
    const pillLocations: Point[] = [];
    if (this.pills && this.pills.children && this.pills.children.entries) {
      this.pills.children.entries.forEach((pill) => {
        const pillSprite = pill as Phaser.GameObjects.GameObject & {
          active?: boolean;
          x: number;
          y: number;
        };
        if (pillSprite && pillSprite.active) {
          pillLocations.push({ x: pillSprite.x, y: pillSprite.y });
        }
      });
    }
    return pillLocations;
  }
}
